import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Tooltip } from "reactstrap";
import { PostData } from "../../../../services/FetchData";
import { toast } from "react-toastify";
import { ProductContext } from "../../../../store";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import Switch from "react-switch";
import { faAdd, faChevronLeft, faChevronRight, faMinus, faPen, faPlus, faSearch, faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "../../../../components/Search/SortableItem";
import { FacetItem } from "../../../../components/Search/FacetItem";
import ReactPaginate from "react-paginate";
import debounce from "lodash.debounce";

// Category/Collection APIs (paths built dynamically from platform): getCategorySearchConfig/getCollectionSearchConfig, getCategoryItems/getCollectionItems, getCategoryItem/getCollectionItem, saveCategorySearchStatus/saveCollectionSearchStatus, saveCategorySearchConfig/saveCollectionSearchConfig, saveCategoryItem/saveCollectionItem, enableCategoryItem/enableCollectionItem, disableCategoryItem/disableCollectionItem, toggleCategoryItemStatus (BC/WC), fetchCategories (BC/WC/Magento), fetchCollections (Shopify)
const PLATFORMS = { MAGENTO: "magento", SHOPIFY: "shopify", BIGCOMMERCE: "bigcommerce", WOOCOMMERCE: "woocommerce", DRUPAL: "drupal" };
const PLATFORM_PREFIX = { [PLATFORMS.MAGENTO]: "magento", [PLATFORMS.SHOPIFY]: "shopify", [PLATFORMS.BIGCOMMERCE]: "bigcommerce", [PLATFORMS.DRUPAL]: "drupal", [PLATFORMS.WOOCOMMERCE]: "woocommerce" };

const GRID_LAYOUTS = [
	{ value: "Auto", label: "Auto" },
	{ value: "1", label: "1" },
	{ value: "2", label: "2" },
	{ value: "3", label: "3" },
	{ value: "4", label: "4" },
	{ value: "5", label: "5" },
	{ value: "6", label: "6" },
];

const getApiConfig = (platform) => {
	const base = platform === PLATFORMS.MAGENTO ? "ms2" : "ms3";
	const prefix = PLATFORM_PREFIX[platform];
	const entity = platform === PLATFORMS.SHOPIFY ? "Collection" : "Category";
	const fetchListPath = () => `api/v1/${prefix}/fetch${entity === "Collection" ? "Collections" : "Categories"}`;
	return { base, prefix, entity, fetchListPath };
};

const Category = () => {
	const { currentWebsiteId, websiteData, websiteLoader } = useContext(ProductContext);
	const [loader, setLoader] = useState(true);
	const [categorySearchConfig, setCategorySearchConfig] = useState(null);
	const [categorySortActiveFields, setCategorySortActiveFields] = useState([]);
	const [categorySortInactiveFields, setCategorySortInactiveFields] = useState([]);
	const [categoryFacetActiveFields, setCategoryFacetActiveFields] = useState([]);
	const [categoryFacetInactiveFields, setCategoryFacetInactiveFields] = useState([]);
	const [categoryItemSearchConfig, setCategoryItemSearchConfig] = useState([]);
	const [categoryItemCount, setCategoryItemCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [categorySearchName, setCategorySearchName] = useState("");
	const [categoryItemStatusLoader, setCategoryItemStatusLoader] = useState(false);
	const [fetchCategoryLoader, setFetchCategoryLoader] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(false);
	const [categoryConfigSave, setCategoryConfigSave] = useState(false);
	const [categoryStatusLoader, setCategoryStatusLoader] = useState(false);
	const [renameModalFlag, setRenameModalFlag] = useState(false);
	const [currentSortField, setCurrentSortField] = useState({});
	const [currentSortIndex, setCurrentSortIndex] = useState(null);
	const [editModalFlag, setEditModalFlag] = useState(false);
	const [currentFacetField, setCurrentFacetField] = useState({});
	const [currentFacetIndex, setCurrentFacetIndex] = useState(null);
	const [customizeCategoryId, setCustomizeCategoryId] = useState(null);
	const [currentCategoryItem, setCurrentCategoryItem] = useState(null);
	const [categorySortActive, setCategorySortActive] = useState([]);
	const [categorySortInactive, setCategorySortInactive] = useState([]);
	const [categoryFacetActive, setCategoryFacetActive] = useState([]);
	const [categoryFacetInactive, setCategoryFacetInactive] = useState([]);
	const [categoryItemLoader, setCategoryItemLoader] = useState(false);
	const [saveCategoryItem, setSaveCategoryItem] = useState(false);
	const [enableLoader, setEnableLoader] = useState(false);
	const [disableLoader, setDisableLoader] = useState(false);
	const [pageChangeFlag, setPageChangeFlag] = useState(false);
	const [paginationLoader, setPaginationLoader] = useState(true);
	const [categoryDetails, setCategoryDetails] = useState({});

	const platform = websiteData?.[currentWebsiteId]?.websiteType;
	const isBigcommerce = platform === PLATFORMS.BIGCOMMERCE;
	const isShopify = platform === PLATFORMS.SHOPIFY;
	const isWooCommerce = platform === PLATFORMS.WOOCOMMERCE;
	const entityLabel = platform === PLATFORMS.SHOPIFY ? "Collections" : "Categories";
	const { base, prefix, entity, fetchListPath } = getApiConfig(platform || "");
	const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

	const getConfigPath = () => `api/v1/${prefix}/get${entity}SearchConfig`;
	const getItemsPath = () => `api/v1/${prefix}/get${entity}Items`;
	const getItemPath = () => `api/v1/${prefix}/get${entity}Item`;
	const saveConfigPath = () => `api/v1/${prefix}/save${entity}SearchConfig`;
	const saveStatusPath = () => `api/v1/${prefix}/save${entity}SearchStatus`;
	const saveItemPath = () => `api/v1/${prefix}/save${entity}Item`;
	const enableItemPath = () => `api/v1/${prefix}/enable${entity}Item`;
	const disableItemPath = () => `api/v1/${prefix}/disable${entity}Item`;

	const idKey = platform === PLATFORMS.SHOPIFY ? "collectionId" : "categoryId";
	const itemSearchIdKey = platform === PLATFORMS.SHOPIFY ? "collectionItemSearchId" : "categoryItemSearchId";
	const hasToggleItemStatus = platform === PLATFORMS.BIGCOMMERCE || platform === PLATFORMS.WOOCOMMERCE;
	const categorySearchIdKey = hasToggleItemStatus ? "categorySearchId" : itemSearchIdKey;

	const enrichItemsWithNames = (items, details) => {
		if (!items?.length) return items;
		const d = details || categoryDetails;
		return items.map((i) => ({
			...i,
			categoryName: i.categoryName ?? i.collectionName ?? d[i.categoryId] ?? d[i.collectionId],
		}));
	};

	const getCategorySearchConfig = useCallback(() => {
		if (!currentWebsiteId || !platform) return;
		setLoader(true);
		// Match old files: Magento sends page, pageSize, name; others only websiteId
		const payload =
			platform === PLATFORMS.MAGENTO
				? { websiteId: currentWebsiteId, page: currentPage + 1, pageSize: itemsPerPage, name: categorySearchName }
				: { websiteId: currentWebsiteId };
		PostData(base, getConfigPath(), payload).then((result) => {
			if (result?.status === "success") {
				const data = result.data;
				const config = data.categorySearchConfig || data.collectionSearchConfig;
				let items = data.categoryItemSearchConfig || data.collectionItemSearchConfig || [];
				const count = data.categoryItemCount ?? data.collectionItemCount ?? 0;
				const details = data.categoryDetails || {};
				setCategoryDetails(details);
				setCategorySearchConfig(config);
				setCategorySortActiveFields(data.categorySortActiveFields || data.collectionSortActiveFields || []);
				setCategorySortInactiveFields(data.categorySortInactiveFields || data.collectionSortInactiveFields || []);
				setCategoryFacetActiveFields(data.categoryFacetActiveFields || data.collectionFacetActiveFields || []);
				setCategoryFacetInactiveFields(data.categoryFacetInactiveFields || data.collectionFacetInactiveFields || []);
				setCategoryItemSearchConfig(enrichItemsWithNames(items, details));
				setCategoryItemCount(count);
			} else {
				toast.error(result?.message || "Failed to load config");
			}
			setLoader(false);
			setPaginationLoader(false);
		});
	}, [currentWebsiteId, platform, base, currentPage, itemsPerPage, categorySearchName]);

	const getCategoryItems = useCallback((searchTerm = null, pageValue = null) => {
		const name = searchTerm !== null ? searchTerm : categorySearchName;
		const pageOneBased = pageValue !== null ? pageValue + 1 : currentPage + 1;
		setPaginationLoader(true);
		// Match old files: Magento uses page, pageSize, name; Bigcommerce/WC use page, limit (no name); Shopify uses page, limit, name
		let payload;
		if (platform === PLATFORMS.MAGENTO) {
			payload = { page: pageOneBased, pageSize: itemsPerPage, name, websiteId: currentWebsiteId };
		} else if (platform === PLATFORMS.SHOPIFY) {
			payload = { websiteId: currentWebsiteId, page: pageOneBased, limit: itemsPerPage, name };
		} else if (platform === PLATFORMS.BIGCOMMERCE || platform === PLATFORMS.WOOCOMMERCE || platform === PLATFORMS.DRUPAL) {
			payload = { websiteId: currentWebsiteId, page: pageOneBased, limit: itemsPerPage };
		} else {
			payload = { websiteId: currentWebsiteId, page: pageOneBased, limit: itemsPerPage, name };
		}
		PostData(base, getItemsPath(), payload).then((result) => {
			if (result?.status === "success") {
				const data = result.data;
				const details = data.categoryDetails || {};
				if (Object.keys(details).length) setCategoryDetails((prev) => ({ ...prev, ...details }));
				let items = data.categoryItemSearchConfig || data.collectionItemSearchConfig || [];
				setCategoryItemSearchConfig(enrichItemsWithNames(items, { ...categoryDetails, ...details }));
				let count = data.categoryItemCount ?? data.collectionItemCount ?? 0;
				// If API returns 0 but we have items (e.g. after fetch), use items length so count matches list
				if (count === 0 && items.length > 0) count = items.length;
				setCategoryItemCount(count);
			}
			setPaginationLoader(false);
		});
	}, [currentWebsiteId, platform, base, currentPage, itemsPerPage, categorySearchName, categoryDetails]);

	useEffect(() => {
		if (!currentWebsiteId || !platform) return;
		getCategorySearchConfig();
	}, [currentWebsiteId, platform]);

	useEffect(() => {
		if (pageChangeFlag) {
			getCategoryItems(null, currentPage);
			setPageChangeFlag(false);
		}
	}, [pageChangeFlag, currentPage]);

	useEffect(() => {
		if (customizeCategoryId !== null) {
			PostData(base, getItemPath(), {
				websiteId: currentWebsiteId,
				...(platform === PLATFORMS.SHOPIFY ? { collectionId: customizeCategoryId } : { categoryId: customizeCategoryId }),
			}).then((result) => {
				if (result?.status === "success") {
					const d = result.data.categoryItem || result.data.collectionItem || result.data;
					setCurrentCategoryItem(d);
					setCategorySortActive(d.categorySortActive || d.collectionSortActive || []);
					setCategorySortInactive(d.categorySortInactive || d.collectionSortInactive || []);
					setCategoryFacetActive(d.categoryFacetActive || d.collectionFacetActive || []);
					setCategoryFacetInactive(d.categoryFacetInactive || d.collectionFacetInactive || []);
				}
				setCategoryItemLoader(false);
			});
		}
	}, [customizeCategoryId, currentWebsiteId, platform, base]);

	const changeCategorySearchStatus = () => {
		setCategoryStatusLoader(true);
		PostData(base, saveStatusPath(), { websiteId: currentWebsiteId, status: categorySearchConfig?.status ?? false }).then((result) => {
			if (result?.status === "success") {
				setCategorySearchConfig((c) => (c ? { ...c, status: !c.status } : null));
				toast.success(categorySearchConfig?.status ? "Category search removed from store front" : "Category search applied to store front");
			} else toast.error(result?.message || "Failed");
			setCategoryStatusLoader(false);
		});
	};

	const saveCategorySearchConfig = () => {
		setCategoryConfigSave(true);
		const payload = {
			...categorySearchConfig,
			websiteId: currentWebsiteId,
			activeSort: categorySortActiveFields,
			inactiveSort: categorySortInactiveFields,
			activeFacet: categoryFacetActiveFields,
			inactiveFacet: categoryFacetInactiveFields,
		};
		PostData(base, saveConfigPath(), payload).then((result) => {
			if (result?.status === "success") toast.success("Settings saved");
			else toast.error(result?.message || "Failed to save");
			setCategoryConfigSave(false);
		});
	};

	const handleCategorySearchConfig = (key, value) => {
		setCategorySearchConfig((c) => (c ? { ...c, [key]: value } : null));
	};

	const findIndexByKey = (arr, key, val) => arr.findIndex((item) => item[key] === val);

	const handleSortDragEnd = (e) => {
		const { active, over } = e;
		if (!over || active.id?.sortFieldId === over.id?.sortFieldId) return;
		const oldI = findIndexByKey(categorySortActiveFields, "sortFieldId", active.id?.sortFieldId);
		const newI = findIndexByKey(categorySortActiveFields, "sortFieldId", over.id?.sortFieldId);
		if (oldI >= 0 && newI >= 0) setCategorySortActiveFields(arrayMove(categorySortActiveFields, oldI, newI));
	};

	const handleFacetDragEnd = (e) => {
		const { active, over } = e;
		if (!over || active.id?.facetFieldId === over.id?.facetFieldId) return;
		const oldI = findIndexByKey(categoryFacetActiveFields, "facetFieldId", active.id?.facetFieldId);
		const newI = findIndexByKey(categoryFacetActiveFields, "facetFieldId", over.id?.facetFieldId);
		if (oldI >= 0 && newI >= 0) setCategoryFacetActiveFields(arrayMove(categoryFacetActiveFields, oldI, newI));
	};

	const handleCategoryItemSortDragEnd = (e) => {
		const { active, over } = e;
		if (!over || active.id?.sortFieldId === over.id?.sortFieldId) return;
		const oldI = findIndexByKey(categorySortActive, "sortFieldId", active.id?.sortFieldId);
		const newI = findIndexByKey(categorySortActive, "sortFieldId", over.id?.sortFieldId);
		if (oldI >= 0 && newI >= 0) setCategorySortActive(arrayMove(categorySortActive, oldI, newI));
	};

	const handleCategoryItemFacetDragEnd = (e) => {
		const { active, over } = e;
		if (!over || active.id?.facetFieldId === over.id?.facetFieldId) return;
		const oldI = findIndexByKey(categoryFacetActive, "facetFieldId", active.id?.facetFieldId);
		const newI = findIndexByKey(categoryFacetActive, "facetFieldId", over.id?.facetFieldId);
		if (oldI >= 0 && newI >= 0) setCategoryFacetActive(arrayMove(categoryFacetActive, oldI, newI));
	};

	const closeSortModal = () => { setCurrentSortField({}); setCurrentSortIndex(null); setRenameModalFlag(false); };
	const closeFacetModal = () => { setCurrentFacetField({}); setCurrentFacetIndex(null); setEditModalFlag(false); };

	const saveSortTitle = () => {
		if (customizeCategoryId != null) {
			const next = [...categorySortActive];
			if (currentSortIndex != null && next[currentSortIndex]) next[currentSortIndex] = { ...next[currentSortIndex], title: currentSortField.title };
			setCategorySortActive(next);
		} else {
			const next = [...categorySortActiveFields];
			if (currentSortIndex != null && next[currentSortIndex]) next[currentSortIndex] = { ...next[currentSortIndex], title: currentSortField.title };
			setCategorySortActiveFields(next);
		}
		closeSortModal();
	};

	const saveFacetValue = () => {
		if (customizeCategoryId != null) {
			const next = [...categoryFacetActive];
			if (currentFacetIndex != null && next[currentFacetIndex]) next[currentFacetIndex] = currentFacetField;
			setCategoryFacetActive(next);
		} else {
			const next = [...categoryFacetActiveFields];
			if (currentFacetIndex != null && next[currentFacetIndex]) next[currentFacetIndex] = currentFacetField;
			setCategoryFacetActiveFields(next);
		}
		closeFacetModal();
	};

	const renameModalTrigger = (index) => { setCurrentSortIndex(index); setCurrentSortField(categorySortActiveFields[index] || {}); setRenameModalFlag(true); };
	const editFacetTrigger = (index) => { setCurrentFacetIndex(index); setCurrentFacetField(categoryFacetActiveFields[index] || {}); setEditModalFlag(true); };

	const renameModalTriggerForItem = (index) => { setCurrentSortIndex(index); setCurrentSortField(categorySortActive[index] || {}); setRenameModalFlag(true); };
	const editFacetTriggerForItem = (index) => { setCurrentFacetIndex(index); setCurrentFacetField(categoryFacetActive[index] || {}); setEditModalFlag(true); };

	const activeCategoryItemSortField = (index) => {
		const inactive = [...categorySortInactive];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...categorySortActive];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setCategorySortActive(active);
		setCategorySortInactive(inactive);
	};

	const deactiveCategoryItemSortField = (index) => {
		const inactive = [...categorySortInactive];
		const active = [...categorySortActive];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setCategorySortActive(active);
		setCategorySortInactive(inactive);
	};

	const activatedCategoryItemFacetField = (index) => {
		const inactive = [...categoryFacetInactive];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...categoryFacetActive];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setCategoryFacetActive(active);
		setCategoryFacetInactive(inactive);
	};

	const deactiveCategoryItemFacetField = (index) => {
		const inactive = [...categoryFacetInactive];
		const active = [...categoryFacetActive];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setCategoryFacetActive(active);
		setCategoryFacetInactive(inactive);
	};

	const activeSortField = (index) => {
		const inactive = [...categorySortInactiveFields];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...categorySortActiveFields];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setCategorySortActiveFields(active);
		setCategorySortInactiveFields(inactive);
	};

	const activatedFacetField = (index) => {
		const inactive = [...categoryFacetInactiveFields];
		const newActive = inactive[index];
		inactive.splice(index, 1);
		const active = [...categoryFacetActiveFields];
		if (newActive) { newActive.order = active.length; active.push(newActive); }
		setCategoryFacetActiveFields(active);
		setCategoryFacetInactiveFields(inactive);
	};

	const deactiveSortField = (index) => {
		const inactive = [...categorySortInactiveFields];
		const active = [...categorySortActiveFields];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setCategorySortActiveFields(active);
		setCategorySortInactiveFields(inactive);
	};

	const deactiveFacetField = (index) => {
		const inactive = [...categoryFacetInactiveFields];
		const active = [...categoryFacetActiveFields];
		const removed = active[index];
		active.splice(index, 1);
		if (removed) inactive.push(removed);
		setCategoryFacetActiveFields(active);
		setCategoryFacetInactiveFields(inactive);
	};

	const priceOptions = [{ label: "Slider", value: "slider" }];
	const otherOptions = [{ label: "Conjunctive", value: "conjunctive" }, { label: "Disjunctive", value: "disjunctive" }];

	const getItemDisplayName = (item) =>
		item?.categoryName ?? item?.collectionName ?? item?.name ?? item?.title
		?? categoryDetails[item?.categoryId] ?? categoryDetails[item?.collectionId]
		?? "";

	const editCategoryItem = (item) => {
		setCurrentCategoryItem(item);
		setCustomizeCategoryId(item[idKey] ?? item.categoryId ?? item.collectionId);
		setCategoryItemLoader(true);
	};

	const goBack = () => {
		setCustomizeCategoryId(null);
		setCurrentCategoryItem(null);
		setCategoryItemLoader(false);
		getCategoryItems();
	};

	const saveCategoryItemFunc = () => {
		setSaveCategoryItem(true);
		const payload = {
			websiteId: currentWebsiteId,
			[idKey]: customizeCategoryId,
			activeSort: categorySortActive,
			inactiveSort: categorySortInactive,
			activeFacet: categoryFacetActive,
			inactiveFacet: categoryFacetInactive,
		};
		PostData(base, saveItemPath(), payload).then((result) => {
			if (result?.status === "success") { toast.success("Saved"); getCategoryItems(); }
			else toast.error(result?.message || "Failed");
			setSaveCategoryItem(false);
		});
	};

	const enableCategoryItem = () => {
		setEnableLoader(true);
		const payload = { websiteId: currentWebsiteId, [idKey]: customizeCategoryId, [itemSearchIdKey]: currentCategoryItem?.[itemSearchIdKey] ?? currentCategoryItem?.categoryItemSearchId ?? currentCategoryItem?.collectionItemSearchId };
		PostData(base, enableItemPath(), payload).then((result) => {
			if (result?.status === "success") {
				getCategoryItems();
				setCurrentCategoryItem((c) => (c ? { ...c, customization: true } : null));
				toast.success(result?.message || `${entity} customisation enabled`);
			} else toast.error(result?.message || "Failed");
			setEnableLoader(false);
		});
	};

	const disableCategoryItem = () => {
		setDisableLoader(true);
		const payload = { websiteId: currentWebsiteId, [idKey]: customizeCategoryId, [itemSearchIdKey]: currentCategoryItem?.[itemSearchIdKey] ?? currentCategoryItem?.categoryItemSearchId ?? currentCategoryItem?.collectionItemSearchId };
		PostData(base, disableItemPath(), payload).then((result) => {
			if (result?.status === "success") {
				getCategoryItems();
				setCurrentCategoryItem((c) => (c ? { ...c, customization: false } : null));
				toast.success(result?.message || `${entity} customisation disabled`);
			} else toast.error(result?.message || "Failed");
			setDisableLoader(false);
		});
	};

	const debouncedSearch = useCallback(debounce((term) => getCategoryItems(term, 0), 500), [platform, currentWebsiteId]);

	const toggleCategoryItemStatus = (categorySearchId, status) => {
		if (!hasToggleItemStatus) return;
		setCategoryItemStatusLoader(true);
		PostData(base, `api/v1/${prefix}/toggleCategoryItemStatus`, { websiteId: currentWebsiteId, status, categorySearchId }).then((result) => {
			if (result?.status === "success") {
				setCategoryItemSearchConfig((prev) => {
					const next = [...prev];
					const idx = next.findIndex((i) => (i.categorySearchId ?? i[categorySearchIdKey]) === categorySearchId);
					if (idx >= 0) next[idx] = { ...next[idx], status: !status };
					return next;
				});
			} else toast.error(result?.message || "Failed");
			setCategoryItemStatusLoader(false);
		});
	};

	// Fetch categories from store (Bigcommerce, WooCommerce, Magento)
	const fetchCategories = () => {
		if (![PLATFORMS.BIGCOMMERCE, PLATFORMS.WOOCOMMERCE, PLATFORMS.MAGENTO].includes(platform)) return;
		setFetchCategoryLoader(true);
		PostData(base, fetchListPath(), { websiteId: currentWebsiteId }).then((result) => {
			if (result?.status === "success") {
				const details = result.data?.categoryDetails || {};
				if (Object.keys(details).length) setCategoryDetails(details);
				// Keep count in sync with fetched categories so header doesn't show 0 after refresh
				const detailsCount = Object.keys(details).length;
				if (detailsCount > 0) setCategoryItemCount(detailsCount);
				setTimeout(() => getCategoryItems(), 20);
			} else toast.error(result?.message || "Failed");
			setFetchCategoryLoader(false);
		});
	};

	// Fetch collections from store (Shopify only)
	const fetchCollections = () => {
		if (platform !== PLATFORMS.SHOPIFY) return;
		setFetchCategoryLoader(true);
		PostData(base, fetchListPath(), { websiteId: currentWebsiteId }).then((result) => {
			if (result?.status === "success") setTimeout(() => getCategoryItems(), 20);
			else toast.error(result?.message || "Failed");
			setFetchCategoryLoader(false);
		});
	};

	const onItemsPerPageChange = (val) => {
		setItemsPerPage(val);
		setCurrentPage(0);
		setPageChangeFlag(true);
	};

	if (websiteLoader || !websiteData?.[currentWebsiteId]) return <DetailSkeleton />;
	if (!platform || !["magento", "shopify", "bigcommerce", "woocommerce", "drupal"].includes(platform)) return <DetailSkeleton />;
	if (loader || !categorySearchConfig) return <DetailSkeleton />;

	const itemsPerPageForCount = itemsPerPage;
	const totalPages = Math.ceil(categoryItemCount / itemsPerPageForCount) || 1;

	return (
		<>
			{!customizeCategoryId && (
				<div className="search-options-card">
					<div className="card-header">
						<div className="title-text">
							<h3>{isBigcommerce ? "Enable ConversionBox Search on Category pages" : `${entity} Search`}</h3>
							<span>{isBigcommerce ? "Enabling ConversionBox Search on Category pages will enable InstantSearch and faceting on your categories." : `Configure default sort and facets for your ${entity.toLowerCase()} pages. You can also customize per ${entity.toLowerCase()}.`}</span>
						</div>
						<div className="switch-container">
							<span>{categorySearchConfig.status ? "Enabled" : "Disabled"}</span>
							{categoryStatusLoader ? <Spinner size="sm" /> : (
								<Switch checked={!!categorySearchConfig.status} onChange={changeCategorySearchStatus} onColor="#9eb3fc" onHandleColor="#3c64f4" handleDiameter={25} uncheckedIcon={false} checkedIcon={false} boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)" activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)" height={15} width={40} className="react-switch" id="category-switch" />
							)}
						</div>
					</div>
					<div className="card-content">
						<Form>
							{isBigcommerce && (
								<>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>No Of Products Shown</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => handleCategorySearchConfig("noOfProducts", Math.max(1, (categorySearchConfig.noOfProducts || 20) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={categorySearchConfig.noOfProducts} min={1} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) handleCategorySearchConfig("noOfProducts", v); }} />
												<Button type="button" className="btn-plus" onClick={() => handleCategorySearchConfig("noOfProducts", (categorySearchConfig.noOfProducts || 20) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
											<div className="buttonContext"><p className="mb-0">Set number of products shown per search. Defaults to 20.</p></div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Product Grid Column</h4></label></Col>
										<Col xs={12} md={6}>
											<Input type="select" value={categorySearchConfig.gridColumnCount ?? categorySearchConfig.gridColumns ?? "Auto"} onChange={(e) => handleCategorySearchConfig("gridColumnCount", e.target.value)}>
												{GRID_LAYOUTS.map((layout) => (
													<option key={layout.value} value={layout.value}>{layout.label}</option>
												))}
											</Input>
											<div className="buttonContext"><p className="mb-0">Products per row for grid view on desktop</p></div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Show Out Of Stock Products</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.outOfStock} onChange={() => handleCategorySearchConfig("outOfStock", !categorySearchConfig.outOfStock)} />
											</div>
											<div className="buttonContext"><p className="mb-0">Products marked as not in stock will show up in search results.</p></div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Show Vendor</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showVendor} onChange={() => handleCategorySearchConfig("showVendor", !categorySearchConfig.showVendor)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-start">
										<Col xs={12} md={4}><label><h4>CSS Selector</h4></label></Col>
										<Col xs={12} md={6}>
											<Input type="text" placeholder="e.g. .page" value={categorySearchConfig.cssSelector || ""} onChange={(e) => handleCategorySearchConfig("cssSelector", e.target.value)} />
											<div className="buttonContext"><p className="mb-0">CSS Selector for hiding the default page content search. You might have to change this value to an element selector that works for your theme. This field is case sensitive and required.</p></div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Show Price</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showPrice} onChange={() => handleCategorySearchConfig("showPrice", !categorySearchConfig.showPrice)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Maximum Title Lines</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => handleCategorySearchConfig("titleMaxLines", Math.max(1, (categorySearchConfig.titleMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={categorySearchConfig.titleMaxLines} min={1} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) handleCategorySearchConfig("titleMaxLines", v); }} />
												<Button type="button" className="btn-plus" onClick={() => handleCategorySearchConfig("titleMaxLines", (categorySearchConfig.titleMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Show Product Description</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showDescription} onChange={() => handleCategorySearchConfig("showDescription", !categorySearchConfig.showDescription)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Show SKU</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showSKU} onChange={() => handleCategorySearchConfig("showSKU", !categorySearchConfig.showSKU)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Enable Quick Preview</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.enableQuickPreview} onChange={() => handleCategorySearchConfig("enableQuickPreview", !categorySearchConfig.enableQuickPreview)} />
											</div>
											<div className="buttonContext"><p className="mb-0">Show a quick product snapshot before opening the full page</p></div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Flip Image On Hover</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.flipImageHover} onChange={() => handleCategorySearchConfig("flipImageHover", !categorySearchConfig.flipImageHover)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-4 align-items-center">
										<Col xs={12} md={4}><label><h4>Infinite/Endless Scrolling</h4></label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.infiniteScroll} onChange={() => handleCategorySearchConfig("infiniteScroll", !categorySearchConfig.infiniteScroll)} />
											</div>
											<div className="buttonContext"><p className="mb-0">Products will be displayed not by pages, but will be loaded on scroll.</p></div>
										</Col>
									</Row>
								</>
							)}
							{isShopify && categorySearchConfig && (
								<>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>CSS Selector</h4></Label></Col>
										<Col xs={12} md={6}>
											<Input type="text" placeholder="e.g. .page" value={categorySearchConfig.cssSelector || ""} onChange={(e) => handleCategorySearchConfig("cssSelector", e.target.value)} />
											<div className="buttonContext"><p className="mb-0">CSS Selector for hiding the default page content search. You might have to change this value to an element selector that works for your theme. This field is case sensitive and required.</p></div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>No Of Products Shown</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => handleCategorySearchConfig("noOfProducts", Math.max(1, (categorySearchConfig.noOfProducts || 20) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={categorySearchConfig.noOfProducts} min={1} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) handleCategorySearchConfig("noOfProducts", v); }} />
												<Button type="button" className="btn-plus" onClick={() => handleCategorySearchConfig("noOfProducts", (categorySearchConfig.noOfProducts || 20) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
											<div className="buttonContext"><p className="mb-0">Set number of products shown per search. Defaults to 20.</p></div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Product Grid Column</h4></Label></Col>
										<Col xs={12} md={6}>
											<Input type="select" value={categorySearchConfig.gridColumnCount ?? categorySearchConfig.gridColumns ?? "Auto"} onChange={(e) => handleCategorySearchConfig("gridColumnCount", e.target.value)}>
												{GRID_LAYOUTS.map((layout) => (
													<option key={layout.value} value={layout.value}>{layout.label}</option>
												))}
											</Input>
											<div className="buttonContext"><p className="mb-0">Products per row for grid view on desktop</p></div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Show Price</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showPrice} onChange={() => handleCategorySearchConfig("showPrice", !categorySearchConfig.showPrice)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Show Out Of Stock Products</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.outOfStock} onChange={() => handleCategorySearchConfig("outOfStock", !categorySearchConfig.outOfStock)} />
											</div>
											<div className="buttonContext"><p className="mb-0">Products marked as not in stock will show up in search results.</p></div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Maximum Title Tiles</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="input-container">
												<Button type="button" className="btn-minus" onClick={() => handleCategorySearchConfig("titleMaxLines", Math.max(1, (categorySearchConfig.titleMaxLines || 2) - 1))}><FontAwesomeIcon icon={faMinus} /></Button>
												<Input type="number" value={categorySearchConfig.titleMaxLines} min={1} onChange={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) handleCategorySearchConfig("titleMaxLines", v); }} />
												<Button type="button" className="btn-plus" onClick={() => handleCategorySearchConfig("titleMaxLines", (categorySearchConfig.titleMaxLines || 2) + 1)}><FontAwesomeIcon icon={faPlus} /></Button>
											</div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Show Product Description</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showDescription} onChange={() => handleCategorySearchConfig("showDescription", !categorySearchConfig.showDescription)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Show SKU</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showSKU} onChange={() => handleCategorySearchConfig("showSKU", !categorySearchConfig.showSKU)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Flip Image On Hover</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.flipImageHover} onChange={() => handleCategorySearchConfig("flipImageHover", !categorySearchConfig.flipImageHover)} />
											</div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Infinite/ Endless Scrolling</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.infiniteScroll} onChange={() => handleCategorySearchConfig("infiniteScroll", !categorySearchConfig.infiniteScroll)} />
											</div>
											<div className="buttonContext"><p className="mb-0">Products will be displayed not by pages, but will be loaded on scroll.</p></div>
										</Col>
									</Row>
									<Row className="mb-5 align-items-start">
										<Col xs={12} md={4}><Label style={{ marginTop: "10px" }}><h4>Show Vendor</h4></Label></Col>
										<Col xs={12} md={6}>
											<div className="checkboxContainer">
												<Input type="checkbox" className="checkboxContainerInput" checked={!!categorySearchConfig.showVendor} onChange={() => handleCategorySearchConfig("showVendor", !categorySearchConfig.showVendor)} />
											</div>
										</Col>
									</Row>
								</>
							)}
							<Row className="mb-4">
								<Col xs={12} md={4}><label><h4>Sort Orders</h4></label></Col>
								<Col xs={12} md={8}>
									{(isBigcommerce || isShopify) && (
										<div className="buttonContext mb-2">
											<p className="mb-0">Enable and configure the different product sorting options. In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
										</div>
									)}
									{(isBigcommerce || isShopify) && <div><b>Active</b></div>}
									{(isBigcommerce || isShopify) && <div className="buttonContext mb-2">Use drag and drop to customise the order.</div>}
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
										<SortableContext items={categorySortActiveFields} strategy={verticalListSortingStrategy}>
											<ul className="cbSortList">
												{categorySortActiveFields.map((id, index) => (
													<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTrigger} onDeactivateBtnClicked={deactiveSortField} />
												))}
											</ul>
										</SortableContext>
									</DndContext>
									{(isBigcommerce || isShopify) && <div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />}
									<h4 className="mt-3">Inactive</h4>
									{categorySortInactiveFields.length > 0 ? (
										<ul style={{ display: "flex", flexWrap: "wrap", ...(isShopify ? { columnGap: "20px", rowGap: "10px" } : { gap: "10px" }) }}>
											{categorySortInactiveFields.map((field, index) => (
												<li key={index}>
													<div className="inactiveSortFIeldWrapper">
														<span title={field.title}>{field.title}</span>
														{isShopify ? (
															<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activeSortField(index); }}>
																<FontAwesomeIcon icon={faPlus} />
															</Button>
														) : (
															<button type="button" onClick={() => activeSortField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
														)}
													</div>
												</li>
											))}
										</ul>
									) : <span>No inactive sort fields</span>}
								</Col>
							</Row>
							<Row className="mb-4">
								<Col xs={12} md={4}><label><h4>Facets</h4></label></Col>
								<Col xs={12} md={8}>
									{(isBigcommerce || isShopify) && <p className="mb-2">Allow customers to explore the catalog by applying multiple filters.</p>}
									{(isBigcommerce || isShopify) && <div><b>Active</b></div>}
									{(isBigcommerce || isShopify) && <div className="buttonContext mb-2">Use drag and drop to customise the order.</div>}
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleFacetDragEnd}>
										<SortableContext items={categoryFacetActiveFields} strategy={verticalListSortingStrategy}>
											<ul className="cbSortList">
												{categoryFacetActiveFields.map((id, index) => (
													<FacetItem key={index} index={index} id={id} onEditClick={editFacetTrigger} onDeactivateBtnClicked={deactiveFacetField} />
												))}
											</ul>
										</SortableContext>
									</DndContext>
									{(isBigcommerce || isShopify) && <div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />}
									<h4 className="mt-3">Inactive</h4>
									{categoryFacetInactiveFields.length > 0 ? (
										<ul style={{ display: "flex", flexWrap: "wrap", ...(isShopify ? { columnGap: "20px", rowGap: "10px" } : { gap: "10px" }) }}>
											{categoryFacetInactiveFields.map((field, index) => (
												<li key={index}>
													<div className="inactiveSortFIeldWrapper">
														<span title={field.title}>{field.title}</span>
														{isShopify ? (
															<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activatedFacetField(index); }}>
																<FontAwesomeIcon icon={faPlus} />
															</Button>
														) : (
															<button type="button" onClick={() => activatedFacetField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
														)}
													</div>
												</li>
											))}
										</ul>
									) : <span>No inactive facet fields</span>}
								</Col>
							</Row>
							<Row className="justify-content-end">
								<Col xs="auto">
									<Button color="primary" onClick={saveCategorySearchConfig} disabled={categoryConfigSave}>
										{categoryConfigSave ? <Spinner size="sm" /> : "Save"}
									</Button>
								</Col>
							</Row>
						</Form>
					</div>
				</div>
			)}

			{!customizeCategoryId ? (
				/* BigCommerce: always show "Your Categories" in list view; when status is off show empty state */
				isBigcommerce ? (
					<div style={{ marginTop: 40 }}>
						<h4 style={{ marginBottom: 16, fontWeight: 700, fontSize: 20, textAlign: "left" }}>
							Your Categories ({categorySearchConfig?.status ? categoryItemCount : 0}){" "}
							{categorySearchConfig?.status && (
								<>
									<Button id="fetchCategoriesTooltip" color="link" disabled={fetchCategoryLoader} onClick={fetchCategories} style={{ padding: 0 }}>
										<FontAwesomeIcon icon={faSync} />
										{fetchCategoryLoader && <Spinner size="sm" style={{ marginLeft: 6 }} />}
									</Button>
									<Tooltip placement="right" isOpen={tooltipOpen} target="fetchCategoriesTooltip" toggle={() => setTooltipOpen(!tooltipOpen)}>
										Fetch the latest categories from BigCommerce
									</Tooltip>
								</>
							)}
						</h4>
						<div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: 24, marginBottom: 24, border: "1px solid #e5e7eb" }}>
							{!categorySearchConfig?.status ? (
								<p className="mb-0 text-muted">Enable ConversionBox Search on Category pages above to view and manage your categories.</p>
							) : paginationLoader ? (
								<DetailSkeleton />
							) : (
								<>
									<table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 16 }} className="align-middle">
										<thead>
											<tr style={{ borderBottom: "2px solid #eee" }}>
												<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Category</th>
												<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Facet & Sort</th>
												<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Status</th>
											</tr>
										</thead>
										<tbody>
											{categoryItemSearchConfig.map((item, index) => (
												<tr key={item[idKey] ?? item.categoryId ?? index} style={{ borderBottom: "1px solid #f5f5f5" }}>
													<td style={{ padding: "12px 10px", fontWeight: 500 }}>{getItemDisplayName(item) || "—"}</td>
													<td style={{ padding: "12px 10px" }}>
														<Button size="sm" color="primary" disabled={!item.status} style={{ minWidth: 60, opacity: item.status ? 1 : 0.5, fontWeight: 600, fontSize: 15 }} onClick={() => item.status && editCategoryItem(item)}>
															<FontAwesomeIcon icon={faPen} style={{ marginRight: 4 }} />Edit
														</Button>
													</td>
													<td style={{ padding: "12px 0", textAlign: "left" }}>
														<Switch checked={!!item.status} onChange={() => toggleCategoryItemStatus(item.categorySearchId ?? item[categorySearchIdKey], item.status)} disabled={categoryItemStatusLoader} onColor="#9eb3fc" onHandleColor="#3c64f4" handleDiameter={20} uncheckedIcon={false} checkedIcon={false} height={15} width={36} className="react-switch" id={`cat-switch-${item.categorySearchId ?? item[categorySearchIdKey] ?? index}`} />
													</td>
												</tr>
											))}
										</tbody>
									</table>
									{categoryItemSearchConfig.length > 0 && (
										<>
											<hr className="my-4" />
											<div className="d-flex justify-content-between align-items-center">
												<div className="d-flex flex-1 align-items-center gap-2">
													<Label style={{ fontSize: 14, fontWeight: 400, marginBottom: 0 }}>Items per page</Label>
													<Input type="select" style={{ width: "auto" }} value={itemsPerPage} onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}>
														<option value={10}>10</option>
														<option value={20}>20</option>
														<option value={50}>50</option>
														<option value={100}>100</option>
													</Input>
												</div>
												{totalPages > 1 && (
													<ReactPaginate nextLabel={<FontAwesomeIcon icon={faChevronRight} />} onPageChange={(e) => { setCurrentPage(e.selected); setPageChangeFlag(true); }} pageRangeDisplayed={3} marginPagesDisplayed={2} pageCount={totalPages} previousLabel={<FontAwesomeIcon icon={faChevronLeft} />} pageClassName="page-item" pageLinkClassName="page-link" previousClassName="page-item" previousLinkClassName="page-link" nextClassName="page-item" nextLinkClassName="page-link" breakLabel="..." breakClassName="page-item" breakLinkClassName="page-link" containerClassName="pagination BannerPagination" activeClassName="active" renderOnZeroPageCount={null} forcePage={currentPage} />
												)}
											</div>
										</>
									)}
								</>
							)}
						</div>
					</div>
				) : isShopify ? (
					/* Shopify: Your Collections with search and Customize link */
					categorySearchConfig?.status && (
						<div style={{ marginTop: 40 }}>
							<h4 style={{ marginBottom: 16, fontWeight: 700, fontSize: 20, textAlign: "left" }}>
								Your Collections ({categoryItemCount}){" "}
								<Button id="fetchCollectionsTooltip" color="link" disabled={fetchCategoryLoader} onClick={fetchCollections} style={{ padding: 0 }}>
									<FontAwesomeIcon icon={faSync} />
									{fetchCategoryLoader && <Spinner size="sm" style={{ marginLeft: 6 }} />}
								</Button>
								<Tooltip placement="right" isOpen={tooltipOpen} target="fetchCollectionsTooltip" toggle={() => setTooltipOpen(!tooltipOpen)}>
									Fetch the latest collections from Shopify
								</Tooltip>
							</h4>
							<div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: 24, marginBottom: 24, border: "1px solid #e5e7eb" }}>
								<div className="searchInputWrapper">
									<InputGroup>
										<InputGroupText className="searchIconInput">
											<FontAwesomeIcon icon={faSearch} />
										</InputGroupText>
										<Input type="text" placeholder="Search Collection..." value={categorySearchName} onChange={(e) => { setCategorySearchName(e.target.value); setCurrentPage(0); debouncedSearch(e.target.value); }} />
									</InputGroup>
								</div>
								{paginationLoader ? (
									<DetailSkeleton />
								) : (
									<>
										<table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 16 }} className="align-middle">
											<thead>
												<tr style={{ borderBottom: "2px solid #eee" }}>
													<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Collection</th>
													<th style={{ textAlign: "right", padding: "12px 10px", fontWeight: 600 }}>Customization</th>
												</tr>
											</thead>
											<tbody>
												{categoryItemSearchConfig.map((item, index) => (
													<tr key={item[idKey] ?? item.collectionId ?? index} style={{ borderBottom: "1px solid #f5f5f5" }}>
														<td style={{ padding: "12px 10px", fontWeight: 500 }}>{getItemDisplayName(item) || "—"}</td>
														<td style={{ padding: "12px 10px", textAlign: "right" }}>
															<Button size="sm" color="link" style={{ minWidth: 60, fontWeight: 600, fontSize: 15 }} onClick={() => editCategoryItem(item)}>
																Customize
															</Button>
														</td>
													</tr>
												))}
											</tbody>
										</table>
										{categoryItemSearchConfig.length > 0 && (
											<>
												<hr className="my-4" />
												<div className="d-flex justify-content-between align-items-center">
													<div className="d-flex flex-1 align-items-center gap-2">
														<Label style={{ fontSize: 14, fontWeight: 400, marginBottom: 0 }}>Items per page</Label>
														<Input type="select" style={{ width: "auto" }} value={itemsPerPage} onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}>
															<option value={10}>10</option>
															<option value={20}>20</option>
															<option value={50}>50</option>
															<option value={100}>100</option>
														</Input>
													</div>
													{totalPages > 1 && (
														<ReactPaginate nextLabel={<FontAwesomeIcon icon={faChevronRight} />} onPageChange={(e) => { setCurrentPage(e.selected); setPageChangeFlag(true); }} pageRangeDisplayed={3} marginPagesDisplayed={2} pageCount={totalPages} previousLabel={<FontAwesomeIcon icon={faChevronLeft} />} pageClassName="page-item" pageLinkClassName="page-link" previousClassName="page-item" previousLinkClassName="page-link" nextClassName="page-item" nextLinkClassName="page-link" breakLabel="..." breakClassName="page-item" breakLinkClassName="page-link" containerClassName="pagination BannerPagination" activeClassName="active" renderOnZeroPageCount={null} forcePage={currentPage} />
													)}
												</div>
											</>
										)}
									</>
								)}
							</div>
						</div>
					)
				) : (
					<div className="search-options-card mt-4">
						<div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-3">
							<h4 className="mb-0">
								{entityLabel} ({categorySearchConfig?.status ? categoryItemCount : 0})
								{(platform === PLATFORMS.WOOCOMMERCE || platform === PLATFORMS.MAGENTO) && categorySearchConfig?.status && (
									<>
										{" "}
										<Button id="fetchCategoriesTooltipOther" color="link" disabled={fetchCategoryLoader} onClick={fetchCategories} style={{ padding: 0 }}>
											<FontAwesomeIcon icon={faSync} />
											{fetchCategoryLoader && <Spinner size="sm" style={{ marginLeft: 6 }} />}
										</Button>
										<Tooltip placement="right" isOpen={tooltipOpen} target="fetchCategoriesTooltipOther" toggle={() => setTooltipOpen(!tooltipOpen)}>
											Fetch the latest categories
										</Tooltip>
									</>
								)}
							</h4>
							<Input type="text" placeholder={`Search ${entityLabel}...`} value={categorySearchName} onChange={(e) => { setCategorySearchName(e.target.value); debouncedSearch(e.target.value); }} style={{ maxWidth: 300 }} />
						</div>
						<div className="card-content">
							<div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: 24, marginBottom: 24, border: "1px solid #e5e7eb" }}>
								{paginationLoader ? (
									<DetailSkeleton />
								) : (
									<>
										<table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 16 }} className="align-middle">
											<thead>
												<tr style={{ borderBottom: "2px solid #eee" }}>
													<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>{platform === PLATFORMS.SHOPIFY ? "Collection" : "Category"}</th>
													<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Facet & Sort</th>
													<th style={{ textAlign: "left", padding: "12px 10px", fontWeight: 600 }}>Status</th>
												</tr>
											</thead>
											<tbody>
												{categoryItemSearchConfig.map((item, index) => (
													<tr key={item[idKey] ?? item.categoryId ?? item.collectionId ?? index} style={{ borderBottom: "1px solid #f5f5f5" }}>
														<td style={{ padding: "12px 10px", fontWeight: 500 }}>{getItemDisplayName(item) || "—"}</td>
														<td style={{ padding: "12px 10px" }}>
															<Button size="sm" color="primary" disabled={hasToggleItemStatus && !item.status} style={{ minWidth: 60, opacity: hasToggleItemStatus && !item.status ? 0.5 : 1, fontWeight: 600, fontSize: 15 }} onClick={() => (hasToggleItemStatus ? item.status && editCategoryItem(item) : editCategoryItem(item))}>
																<FontAwesomeIcon icon={faPen} style={{ marginRight: 4 }} />Edit
															</Button>
														</td>
														<td style={{ padding: "12px 0", textAlign: "left" }}>
															{hasToggleItemStatus ? (
																<Switch checked={!!item.status} onChange={() => toggleCategoryItemStatus(item.categorySearchId ?? item[categorySearchIdKey], item.status)} disabled={categoryItemStatusLoader} onColor="#9eb3fc" onHandleColor="#3c64f4" handleDiameter={20} uncheckedIcon={false} checkedIcon={false} height={15} width={36} className="react-switch" id={`cat-switch-${item.categorySearchId ?? item[categorySearchIdKey] ?? index}`} />
															) : (
																<span>{item.customization ? "Customized" : "Default"}</span>
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
										{categoryItemSearchConfig.length > 0 && (
											<>
												<hr className="my-4" />
												<div className="d-flex justify-content-between align-items-center">
													<div className="d-flex flex-1 align-items-center gap-2">
														<Label style={{ fontSize: 14, fontWeight: 400, marginBottom: 0 }}>Items per page</Label>
														<Input type="select" style={{ width: "auto" }} value={itemsPerPage} onChange={(e) => onItemsPerPageChange(parseInt(e.target.value, 10))}>
															<option value={10}>10</option>
															<option value={20}>20</option>
															<option value={50}>50</option>
															<option value={100}>100</option>
														</Input>
													</div>
													{totalPages > 1 && (
														<ReactPaginate nextLabel={<FontAwesomeIcon icon={faChevronRight} />} onPageChange={(e) => { setCurrentPage(e.selected); setPageChangeFlag(true); }} pageRangeDisplayed={3} marginPagesDisplayed={2} pageCount={totalPages} previousLabel={<FontAwesomeIcon icon={faChevronLeft} />} pageClassName="page-item" pageLinkClassName="page-link" previousClassName="page-item" previousLinkClassName="page-link" nextClassName="page-item" nextLinkClassName="page-link" breakLabel="..." breakClassName="page-item" breakLinkClassName="page-link" containerClassName="pagination BannerPagination" activeClassName="active" renderOnZeroPageCount={null} forcePage={currentPage} />
													)}
												</div>
											</>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				)
			) : (isBigcommerce || isWooCommerce) ? (
				/* BigCommerce / WooCommerce edit view - full layout with Enable card, Sort, Facets, Disable */
				<div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px" }}>
					{categoryItemLoader ? (
						<DetailSkeleton />
					) : (
						<>
							<Row>
								<Col xs={12}>
									<Button color="link" disabled={saveCategoryItem || disableLoader} onClick={goBack} style={{ textDecoration: "none", padding: 0, marginBottom: "15px" }}>
										<FontAwesomeIcon icon={faChevronLeft} size="1x" className="me-2" /> Back
									</Button>
								</Col>
							</Row>
							<Row className="align-items-center justify-content-between">
								<Col xs={12} md={6}>
									<h3>{categoryDetails[customizeCategoryId] ?? getItemDisplayName(currentCategoryItem) ?? customizeCategoryId}</h3>
								</Col>
								<Col xs={12} md="auto">
									<span style={{ color: "#888", fontSize: 16, fontWeight: 400, marginTop: 2 }}>Category</span>
								</Col>
							</Row>
							<div className="mt-2 mb-5"><hr /></div>
							{!currentCategoryItem?.customization ? (
								<>
									<Card className="p-4 rounded" style={{ maxWidth: "850px", marginLeft: "auto", marginRight: "auto" }}>
										<div>
											<div className="mb-1">
												<h2 className="h5 fw-medium">Customise the behaviour of this category</h2>
											</div>
											<div className="mb-4">
												<div className="toggleContainer">
													<div className="d-flex justify-content-between">
														<p className="mb-0">If you allow customizations on this category page, you can configure it to behave differently than others, i.e., create an exception out of general category page settings.</p>
													</div>
												</div>
											</div>
											<div className="d-flex justify-content-end">
												<Button color="primary" disabled={enableLoader} onClick={enableCategoryItem}>
													{enableLoader ? <Spinner size="sm" /> : "Enable"}
												</Button>
											</div>
										</div>
									</Card>
									<div className="inactiveGrid mt-5">
										<Container fluid className="mb-4">
											<Row>
												<Col md={4} className="pe-4">
													<p className="fw-bold text-uppercase small mb-2">Sort orders</p>
													<p className="small mb-2">Enable and configure the different product sorting options.</p>
													<p className="small mb-5">In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
												</Col>
												<Col md={8}>
													<Card>
														<CardBody className="p-5">
															<div className="d-flex justify-content-center align-items-center h-100">
																<span>Allow customisation first</span>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>
										</Container>
										<Container fluid className="mb-4">
											<Row>
												<Col md={4} className="pe-4">
													<p className="fw-bold text-uppercase small mb-2">Facets</p>
													<p className="small mb-2">Allow customers to explore the catalog by applying multiple filters.</p>
												</Col>
												<Col md={8}>
													<Card>
														<CardBody className="p-5">
															<div className="d-flex justify-content-center align-items-center h-100">
																<span>Allow customisation first</span>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>
										</Container>
									</div>
								</>
							) : (
								<>
									<Card className="p-4 bg-light">
										<div className="searchOptionFormLayoutWrapper">
											<Form>
												<Row className="mb-4">
													<Col xs={12} md={4}>
														<Label style={{ marginTop: "10px" }}><h4>Sort orders</h4></Label>
													</Col>
													<Col xs={12} md={8}>
														<div style={{ display: "flex", flexDirection: "column" }}>
															<p>Enable and configure the different product sorting options.</p>
															<p>In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
															<h4 style={{ marginBottom: "10px" }}>Active</h4>
															<p>Use drag and drop to customise the order.</p>
															<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemSortDragEnd}>
																<SortableContext items={categorySortActive} strategy={verticalListSortingStrategy}>
																	<ul className="cbSortList">
																		{categorySortActive.map((id, index) => (
																			<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemSortField} />
																		))}
																	</ul>
																</SortableContext>
															</DndContext>
															<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
															<h4>Inactive</h4>
															<div style={{ marginTop: "10px" }}>
																{categorySortInactive.length > 0 ? (
																	<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
																		{categorySortInactive.map((field, index) => (
																			<li key={index}>
																				<div className="inactiveSortFIeldWrapper">
																					<span title={field.title}>{field.title}</span>
																					<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activeCategoryItemSortField(index); }}>
																						<FontAwesomeIcon icon={faPlus} />
																					</Button>
																				</div>
																			</li>
																		))}
																	</ul>
																) : (
																	<span>No inactive sort fields</span>
																)}
															</div>
														</div>
													</Col>
												</Row>
												<Row className="mb-4 mt-5">
													<Col xs={12} md={4}>
														<Label style={{ marginTop: "10px" }}><h4>Facets</h4></Label>
													</Col>
													<Col xs={12} md={8}>
														<div style={{ display: "flex", flexDirection: "column" }}>
															<p>Allow customers to explore the catalog by applying multiple filters.</p>
															<h4 style={{ marginBottom: "10px" }}>Active</h4>
															<p>Use drag and drop to customise the order.</p>
															<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemFacetDragEnd}>
																<SortableContext items={categoryFacetActive} strategy={verticalListSortingStrategy}>
																	<ul className="cbSortList">
																		{categoryFacetActive.map((id, index) => (
																			<FacetItem key={index} index={index} id={id} onEditClick={editFacetTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemFacetField} />
																		))}
																	</ul>
																</SortableContext>
															</DndContext>
															<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
															<h4>Inactive</h4>
															<div style={{ marginTop: "10px" }}>
																{categoryFacetInactive.length > 0 ? (
																	<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
																		{categoryFacetInactive.map((field, index) => (
																			<li key={index}>
																				<div className="inactiveSortFIeldWrapper">
																					<span title={field.title}>{field.title}</span>
																					<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activatedCategoryItemFacetField(index); }}>
																						<FontAwesomeIcon icon={faPlus} />
																					</Button>
																				</div>
																			</li>
																		))}
																	</ul>
																) : (
																	<span>No inactive facet fields</span>
																)}
															</div>
														</div>
													</Col>
												</Row>
												<Row className="justify-content-end">
													<Col xs="auto">
														<Button type="button" color="primary" onClick={saveCategoryItemFunc} disabled={saveCategoryItem || disableLoader}>
															{saveCategoryItem ? <Spinner size="sm" /> : "Save"}
														</Button>
													</Col>
												</Row>
											</Form>
										</div>
									</Card>
									<Card className="mb-5 mt-5">
										<CardBody>
											<h3 className="h5 fw-medium">Customise the behaviour of this category</h3>
											<p className="mb-4">If you allow customisations on this category page, you can configure it to behave differently than others, i.e., create an exception out of general collection page settings.</p>
											<div className="d-flex justify-content-end mt-5">
												<div className="d-flex">
													<Button color="danger" onClick={disableCategoryItem} disabled={disableLoader || saveCategoryItem}>
														{disableLoader && <Spinner size="sm" className="me-2" />}
														Disable
													</Button>
												</div>
											</div>
										</CardBody>
									</Card>
								</>
							)}
						</>
					)}
				</div>
			) : isShopify ? (
				/* Shopify edit view - same layout as BC with collection wording */
				<div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 16px" }}>
					{categoryItemLoader ? (
						<DetailSkeleton />
					) : (
						<>
							<Row>
								<Col xs={12}>
									<Button color="link" disabled={saveCategoryItem || disableLoader} onClick={goBack} style={{ textDecoration: "none", padding: 0, marginBottom: "15px" }}>
										<FontAwesomeIcon icon={faChevronLeft} size="1x" className="me-2" /> Back
									</Button>
								</Col>
							</Row>
							<Row className="align-items-center justify-content-between">
								<Col xs={12} md={6}>
									<h3>{categoryDetails[customizeCategoryId] ?? getItemDisplayName(currentCategoryItem) ?? customizeCategoryId}</h3>
								</Col>
								<Col xs={12} md="auto">
									<span style={{ color: "#888", fontSize: 16, fontWeight: 400, marginTop: 2 }}>Collection</span>
								</Col>
							</Row>
							<div className="mt-2 mb-5"><hr /></div>
							{!currentCategoryItem?.customization ? (
								<>
									<Card className="p-4 rounded" style={{ maxWidth: "850px", marginLeft: "auto", marginRight: "auto" }}>
										<div>
											<div className="mb-1">
												<h2 className="h5 fw-medium">Customise the behaviour of this collection</h2>
											</div>
											<div className="mb-4">
												<div className="toggleContainer">
													<div className="d-flex justify-content-between">
														<p className="mb-0">If you allow customizations on this collection page, you can configure it to behave differently than others, i.e., create an exception out of general collection page settings.</p>
													</div>
												</div>
											</div>
											<div className="d-flex justify-content-end">
												<Button color="primary" disabled={enableLoader} onClick={enableCategoryItem}>
													{enableLoader ? <Spinner size="sm" /> : "Enable"}
												</Button>
											</div>
										</div>
									</Card>
									<div className="inactiveGrid mt-5">
										<Container fluid className="mb-4">
											<Row>
												<Col md={4} className="pe-4">
													<p className="fw-bold text-uppercase small mb-2">Sort orders</p>
													<p className="small mb-2">Enable and configure the different product sorting options.</p>
													<p className="small mb-5">In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
												</Col>
												<Col md={8}>
													<Card>
														<CardBody className="p-5">
															<div className="d-flex justify-content-center align-items-center h-100">
																<span>Allow customisation first</span>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>
										</Container>
										<Container fluid className="mb-4">
											<Row>
												<Col md={4} className="pe-4">
													<p className="fw-bold text-uppercase small mb-2">Facets</p>
													<p className="small mb-2">Allow customers to explore the catalog by applying multiple filters.</p>
												</Col>
												<Col md={8}>
													<Card>
														<CardBody className="p-5">
															<div className="d-flex justify-content-center align-items-center h-100">
																<span>Allow customisation first</span>
															</div>
														</CardBody>
													</Card>
												</Col>
											</Row>
										</Container>
									</div>
								</>
							) : (
								<>
									<Card className="p-4 bg-light">
										<div className="searchOptionFormLayoutWrapper">
											<Form>
												<Row className="mb-4">
													<Col xs={12} md={4}>
														<Label style={{ marginTop: "10px" }}><h4>Sort orders</h4></Label>
													</Col>
													<Col xs={12} md={8}>
														<div style={{ display: "flex", flexDirection: "column" }}>
															<p>Enable and configure the different product sorting options.</p>
															<p>In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
															<h4 style={{ marginBottom: "10px" }}>Active</h4>
															<p>Use drag and drop to customise the order.</p>
															<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemSortDragEnd}>
																<SortableContext items={categorySortActive} strategy={verticalListSortingStrategy}>
																	<ul className="cbSortList">
																		{categorySortActive.map((id, index) => (
																			<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemSortField} />
																		))}
																	</ul>
																</SortableContext>
															</DndContext>
															<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
															<h4>Inactive</h4>
															<div style={{ marginTop: "10px" }}>
																{categorySortInactive.length > 0 ? (
																	<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
																		{categorySortInactive.map((field, index) => (
																			<li key={index}>
																				<div className="inactiveSortFIeldWrapper">
																					<span title={field.title}>{field.title}</span>
																					<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activeCategoryItemSortField(index); }}>
																						<FontAwesomeIcon icon={faPlus} />
																					</Button>
																				</div>
																			</li>
																		))}
																	</ul>
																) : (
																	<span>No inactive sort fields</span>
																)}
															</div>
														</div>
													</Col>
												</Row>
												<Row className="mb-4 mt-5">
													<Col xs={12} md={4}>
														<Label style={{ marginTop: "10px" }}><h4>Facets</h4></Label>
													</Col>
													<Col xs={12} md={8}>
														<div style={{ display: "flex", flexDirection: "column" }}>
															<p>Allow customers to explore the catalog by applying multiple filters.</p>
															<h4 style={{ marginBottom: "10px" }}>Active</h4>
															<p>Use drag and drop to customise the order.</p>
															<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemFacetDragEnd}>
																<SortableContext items={categoryFacetActive} strategy={verticalListSortingStrategy}>
																	<ul className="cbSortList">
																		{categoryFacetActive.map((id, index) => (
																			<FacetItem key={index} index={index} id={id} onEditClick={editFacetTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemFacetField} />
																		))}
																	</ul>
																</SortableContext>
															</DndContext>
															<div style={{ width: "100%", border: "1px solid lightgrey", marginBottom: "1rem", marginTop: "1rem" }} />
															<h4>Inactive</h4>
															<div style={{ marginTop: "10px" }}>
																{categoryFacetInactive.length > 0 ? (
																	<ul style={{ display: "flex", columnGap: "20px", rowGap: "10px", flexWrap: "wrap" }}>
																		{categoryFacetInactive.map((field, index) => (
																			<li key={index}>
																				<div className="inactiveSortFIeldWrapper">
																					<span title={field.title}>{field.title}</span>
																					<Button size="sm" color="primary" onClick={(e) => { e.preventDefault(); activatedCategoryItemFacetField(index); }}>
																						<FontAwesomeIcon icon={faPlus} />
																					</Button>
																				</div>
																			</li>
																		))}
																	</ul>
																) : (
																	<span>No inactive facet fields</span>
																)}
															</div>
														</div>
													</Col>
												</Row>
												<Row className="justify-content-end">
													<Col xs="auto">
														<Button type="button" color="primary" onClick={saveCategoryItemFunc} disabled={saveCategoryItem || disableLoader}>
															{saveCategoryItem ? <Spinner size="sm" /> : "Save"}
														</Button>
													</Col>
												</Row>
											</Form>
										</div>
									</Card>
									<Card className="mb-5 mt-5">
										<CardBody>
											<h3 className="h5 fw-medium">Customise the behaviour of this collection</h3>
											<p className="mb-4">If you allow customisations on this collection page, you can configure it to behave differently than others, i.e., create an exception out of general collection page settings.</p>
											<div className="d-flex justify-content-end mt-5">
												<div className="d-flex">
													<Button color="danger" onClick={disableCategoryItem} disabled={disableLoader || saveCategoryItem}>
														{disableLoader && <Spinner size="sm" className="me-2" />}
														Disable
													</Button>
												</div>
											</div>
										</CardBody>
									</Card>
								</>
							)}
						</>
					)}
				</div>
			) : (
				/* Non-BigCommerce edit view (Magento, WooCommerce, etc.) */
				<div className="search-options-card mt-4">
					<div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
						<div className="d-flex align-items-center gap-3">
							<Button color="link" onClick={goBack} className="p-0" style={{ fontWeight: 500 }}>← Back</Button>
							<h3 className="mb-0">{getItemDisplayName(currentCategoryItem) || customizeCategoryId}</h3>
							<span className="text-muted" style={{ fontSize: "1rem" }}>{platform === PLATFORMS.SHOPIFY ? "Collection" : "Category"}</span>
						</div>
					</div>
					<div className="card-content">
						{categoryItemLoader ? <Spinner /> : (
							<>
								{currentCategoryItem?.customization !== true && (
									<div className="mb-4">
										<Button color="success" onClick={enableCategoryItem} disabled={enableLoader}>{enableLoader ? <Spinner size="sm" /> : "Enable customization"}</Button>
									</div>
								)}
								{currentCategoryItem?.customization === true && (
									<>
										<div className="mb-4">
											<h4 className="mb-2">Sort orders</h4>
											<div className="buttonContext mb-2">
												<p className="mb-0">Enable and configure the different product sorting options. In order to deliver our speed, our ranking is done at indexing time, which means that for each active sort order we create a new index which contains the same amount of records as the main one.</p>
											</div>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemSortDragEnd}>
												<SortableContext items={categorySortActive} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{categorySortActive.map((id, index) => (
															<SortableItem key={index} index={index} id={id} onRenameClick={renameModalTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemSortField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<h4 className="mt-3">Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{categorySortInactive.length > 0 ? (
													<ul style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
														{categorySortInactive.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activeCategoryItemSortField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive sort fields</span>
												)}
											</div>
										</div>
										<div className="mb-4">
											<h4 className="mb-2">Facets</h4>
											<p className="mb-2">Allow customers to explore the catalog by applying multiple filters.</p>
											<div><b>Active</b></div>
											<div className="buttonContext mb-2">Use drag and drop to customise the order.</div>
											<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryItemFacetDragEnd}>
												<SortableContext items={categoryFacetActive} strategy={verticalListSortingStrategy}>
													<ul className="cbSortList">
														{categoryFacetActive.map((id, index) => (
															<FacetItem key={index} index={index} id={id} onEditClick={editFacetTriggerForItem} onDeactivateBtnClicked={deactiveCategoryItemFacetField} />
														))}
													</ul>
												</SortableContext>
											</DndContext>
											<h4 className="mt-3">Inactive</h4>
											<div style={{ marginTop: "10px" }}>
												{categoryFacetInactive.length > 0 ? (
													<ul style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
														{categoryFacetInactive.map((field, index) => (
															<li key={index}>
																<div className="inactiveSortFIeldWrapper">
																	<span title={field.title}>{field.title}</span>
																	<button type="button" onClick={() => activatedCategoryItemFacetField(index)}><FontAwesomeIcon icon={faAdd} size="1x" color="#c9c9c9" /></button>
																</div>
															</li>
														))}
													</ul>
												) : (
													<span>No inactive facet fields</span>
												)}
											</div>
											<Row className="justify-content-end mt-3">
												<Col xs="auto">
													<Button color="primary" onClick={saveCategoryItemFunc} disabled={saveCategoryItem}>{saveCategoryItem ? <Spinner size="sm" /> : "Save"}</Button>
												</Col>
											</Row>
										</div>
										<div className="mb-4">
											<h4 className="mb-2">Customise the behaviour of this category</h4>
											<p className="mb-2 text-muted">If you allow customisations on this category page, you can configure it to behave differently than others, i.e., create an exception out of general collection page settings.</p>
											<div className="d-flex justify-content-end">
												<Button color="danger" onClick={disableCategoryItem} disabled={disableLoader}>{disableLoader ? <Spinner size="sm" /> : "Disable"}</Button>
											</div>
										</div>
									</>
								)}
							</>
						)}
					</div>
				</div>
			)}

			{renameModalFlag && (
				<Modal isOpen={renameModalFlag} toggle={closeSortModal} centered>
					<ModalHeader toggle={closeSortModal}>Rename sort order</ModalHeader>
					<ModalBody>
						<FormGroup>
							<Label>Title</Label>
							<Input type="text" value={currentSortField.title || ""} onChange={(e) => setCurrentSortField((f) => ({ ...f, title: e.target.value }))} />
						</FormGroup>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={closeSortModal}>Cancel</Button>
						<Button color="primary" onClick={saveSortTitle}>Save</Button>
					</ModalFooter>
				</Modal>
			)}

			{editModalFlag && (
				<Modal isOpen={editModalFlag} toggle={closeFacetModal} centered>
					<ModalHeader toggle={closeFacetModal}>Edit facet</ModalHeader>
					<ModalBody>
						<FormGroup>
							<Label>Title</Label>
							<Input type="text" value={currentFacetField.title || ""} onChange={(e) => setCurrentFacetField((f) => ({ ...f, title: e.target.value }))} />
						</FormGroup>
						<FormGroup>
							<Label>Facet type</Label>
							<Input type="select" value={currentFacetField.facetType || ""} onChange={(e) => setCurrentFacetField((f) => ({ ...f, facetType: e.target.value }))}>
								{currentFacetField.attribute === "price" ? priceOptions.map((o, i) => <option key={i} value={o.value}>{o.label}</option>) : otherOptions.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
							</Input>
						</FormGroup>
						<FormGroup check>
							<Input type="checkbox" checked={!!currentFacetField.searchable} onChange={(e) => setCurrentFacetField((f) => ({ ...f, searchable: e.target.checked }))} />
							<Label check>Searchable facet</Label>
						</FormGroup>
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={closeFacetModal}>Cancel</Button>
						<Button color="primary" onClick={saveFacetValue}>Save</Button>
					</ModalFooter>
				</Modal>
			)}
		</>
	);
};

export default Category;
